import isEmpty from 'lodash/isEmpty'
import { ChangeEvent, FC, useEffect, useState } from 'react'
import Modal from 'react-modal'
import { useDispatch, useSelector, useStore } from 'react-redux'
import { useNavigate } from 'react-router'
import { validateDocumentDetails } from '../../Pages/EntryForm/validation'
import etrBackend from '../../api/etrBackend'
import errorAlert from '../../assets/images/error-alert.svg'
import success from '../../assets/images/success.svg'
import { ETR_STATUS, server } from '../../constants'
import { ERROR_MESSAGE } from '../../constants/errorMessage'
import { isBranchStaff } from '../../helpers/roles'
import { DisplayStatusName } from '../../helpers/texthelper'
import { ConfirmModalProps, DocumentDetailErrors } from '../../models/component'
import { DocumentModel } from '../../models/document'
import { DocumentDetailModel } from '../../models/documentDetail'
import * as documentAction from '../../redux/actions/document.detail.action'
import * as editActions from '../../redux/actions/transaction.edit.action'
import { RootReducers } from '../../redux/reducers'
import { LoadingOverlay } from '../loadingOverlay'
import Classes from './ModalConfirmPaid.module.css'
import { checkSubmissionDelay as _checkSubmissionDelay } from '../../helpers/date'

const ErrorComponent: FC<{
  errors: DocumentDetailErrors[]
  errorAmount: string[]
}> = ({ errorAmount, errors }) => {
  const documentData = useSelector((state: RootReducers) => state.documentDetailReducer)

  const getErrorTitle = (method: string) => {
    return method === 'detail' ? 'ข้อมูลกรมธรรม์ลูกค้า' : `การชำระโดย ${method}`
  }

  if (documentData.documentModel?.status !== ETR_STATUS.NEW_ETR) {
    return (
      <div className="error-text-validate">
        <img src={errorAlert} alt="ErrorAlert" />
        {`ไม่สามารถทำรายการได้เนื่องจากรายการนี้ได้ถูกดำเนินการปรับสถานะเป็น "${DisplayStatusName(
          documentData.documentModel?.status
        )}" แล้ว`}
      </div>
    )
  } else if (errors.length) {
    return (
      <div className="errors">
        {' '}
        {errors.map(err => (
          <div className="error-wrapper" key={err.rt}>
            <div className="errRtNo">
              รายการเลขที่{' '}
              <a className="rtno" href={`/documentDetail/${err.documentId}`}>
                {err.rt}
              </a>{' '}
              ข้อมูลไม่สมบูรณ์
            </div>
            {err.details.map(detail => (
              <div key={detail.method} className="error">
                <div className="error-name">{getErrorTitle(detail.method)}</div>
                <ul>
                  {detail.errors.map(errItem => (
                    <li key={errItem.key} className="error-desc">
                      {errItem.error}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        ))}
      </div>
    )
  } else if (errorAmount.length) {
    return (
      <>
        {errorAmount.map(err => (
          <div className="errorAmount" key={err}>
            {err}
          </div>
        ))}
      </>
    )
  } else {
    return null
  }
}

const SubmitDocumentList: FC<{ documentSubmit: DocumentModel[] }> = ({ documentSubmit }) => {
  if (!documentSubmit.length) {
    return null
  }

  return (
    <div className="submitList">
      <div className="submitListLabel">รายการที่จะถูกนำเข้าไปพร้อมกัน</div>
      <ul>
        {documentSubmit.map(item => (
          <li className="submitListRTNo" key={item.id}>
            {item.receiptNo}
          </li>
        ))}
      </ul>
    </div>
  )
}

export const ConfirmModal: React.VFC<ConfirmModalProps> = ({
  data,
  handleCancelbutton,
  isOpenModal
}) => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const store = useStore<RootReducers>()
  const loginData = useSelector((state: RootReducers) => state.loginReducer)
  const [errors, setErrors] = useState<DocumentDetailErrors[]>([])
  const [isFetching, setIsFetching] = useState(false)
  const [documentSubmit, setDocumentSubmit] = useState<DocumentModel[]>([])
  const [errorAmount, setErrorAmount] = useState<string[]>([])
  const [submissionDelayReason, setSubmissionDelayReason] = useState('')
  const [isShowSubmissionDelayReason, setIsShowSubmissionDelayReason] = useState(false)
  const _isBranchStaff = isBranchStaff(loginData.account?.agentInfo.role as string)
  const disableStatus = ![ETR_STATUS.NEW_ETR].includes(
    store.getState().documentDetailReducer.documentModel?.status || ''
  )
  const documentModel = useSelector(
    (state: RootReducers) => state.documentDetailReducer.documentModel
  )

  const disableSubmitBtn =
    errors.length > 0 ||
    errorAmount.length > 0 ||
    disableStatus ||
    (isShowSubmissionDelayReason && submissionDelayReason === '')

  const checkSubmissionDelay = async () => {
    const paymentObjectives = documentModel?.paymentObjectives?.[0]
    const receiptdate = documentModel?.receiptdate
    const isDelay = await _checkSubmissionDelay({
      paymentObjectives: paymentObjectives?.name,
      receiptdate
    })

    setIsShowSubmissionDelayReason(isDelay)
  }

  const getAllDocumentByChequeNumber = (documentDetails: DocumentDetailModel[]) => {
    if (documentDetails?.length) {
      const data: any[] = []
      documentDetails.map(item => {
        data.push({
          chequeNumber: item.cheque_Number,
          paymentMethodId: item.paymentMethod.id
        })
      })

      return etrBackend.post<DocumentModel[]>(
        server.TRANSACTION_URL + '/getAllDocumentByChequeNumber',
        {
          data
        }
      )
    }

    return Promise.resolve({ data: [] })
  }

  const validateErrors = (documents: DocumentModel[]) => {
    const errors: DocumentDetailErrors[] = []

    documents.forEach(document => {
      const addError = (key: string, error: string) => {
        errors.push({
          rt: document.receiptNo as string,
          documentId: document.id as number,
          details: [
            {
              method: 'detail',
              errors: [{ key, error }]
            }
          ]
        })
      }

      if (store.getState().loginReducer.account?.agentInfo.isAO === true || _isBranchStaff) {
        if (isEmpty(document?.policyNo) && isEmpty(document?.NB_PolicyNo)) {
          addError('policyNo', 'ไม่มีข้อมูลเลขที่กรมธรรม์')
        }
      } else if (!document?.policyNo && !document?.NB_PolicyNo) {
        addError('NB_PolicyNo', 'เลขที่กรมธรรม์รายใหม่')
      }

      if (_isBranchStaff) {
        if (document.hasChangeEmailOrMobile === true && isEmpty(document.changeEmailOrMobileFile)) {
          addError('hasChangeEmailOrMobile', ERROR_MESSAGE.REQUIRED_HAS_CHANGE_EMAIL_OR_MOBILE)
        }
      } else {
        document.documentDetails?.forEach(documentDetail => {
          const validateErrors = validateDocumentDetails(documentDetail)
          if (validateErrors?.length) {
            const rtIndex = errors.findIndex(item => item.rt === document.receiptNo)
            const error = {
              method: documentDetail.paymentMethod.name,
              errors: validateErrors
            }
            if (rtIndex > -1) {
              errors[rtIndex].details.push(error)
            } else {
              errors.push({
                rt: document.receiptNo as string,
                documentId: document.id as number,
                details: [error]
              })
            }
          }
        })
      }
    })

    if (errors.length) {
      setErrors(errors)
      return false
    } else {
      setDocumentSubmit([...documents].filter(item => item.id !== data.id))
      return true
    }
  }

  const getDocument = async () => {
    if (data.id) {
      try {
        setIsFetching(true)
        setDocumentSubmit([])
        await dispatch(documentAction.handleGetDocumentById(String(data.id)))
        const documentData = store.getState().documentDetailReducer
        const ckcList = documentData.documentModel?.documentDetails?.filter(item =>
          [2, 6].includes(item.paymentMethod.id)
        )
        const validateDocs = []

        if (ckcList?.length) {
          const { data: documents } = await getAllDocumentByChequeNumber(ckcList)
          validateDocs.push(...documents)
        } else {
          validateDocs.push(documentData.documentModel as DocumentModel)
        }

        const valid = validateErrors(validateDocs)

        if (valid && ckcList?.length) {
          validateCKCAmoumt(
            ckcList,
            validateDocs.filter(item => item.id !== documentData.documentModel?.id)
          )
        }

        await checkSubmissionDelay()
      } catch (error) {
        handleCancelbutton()
        console.error(error)
      } finally {
        setIsFetching(false)
      }
    }
  }

  const validateCKCAmoumt = (
    documentDetails: DocumentDetailModel[],
    documents: DocumentModel[]
  ) => {
    const factor = 1000
    const tempError = []
    for (const detail of documentDetails) {
      const paymentTotalAmount = Number(detail.paymentTotalAmount)
      let total = Math.round(Number(detail.paymentAmount) * factor)
      for (const document of documents) {
        for (const detail2 of document.documentDetails ?? []) {
          if (detail2.paymentMethod.id === detail.paymentMethod.id) {
            total += Math.round(Number(detail2.paymentAmount) * factor)
          }
        }
      }

      total = total / factor

      if (total !== paymentTotalAmount) {
        tempError.push(
          `${detail.paymentMethod.name}ทุกรายการรวม ${total} จากทั้งหมด ${paymentTotalAmount} กรุณาตรวจสอบข้อมูล`
        )
      }
    }

    setErrorAmount(tempError)
  }

  const handleSubmissionDelayReasonChnage = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setSubmissionDelayReason(e.target.value)
  }

  const handleConfirmOk = async () => {
    try {
      if (data.id) {
        handleCancelbutton(true)

        const params: any = {
          reason: data.reasonText ?? '',
          relatedDocuments: [...documentSubmit.map(item => item.id as number), data.id],
          submissionDelayReason: ''
        }

        if (isShowSubmissionDelayReason) {
          params.submissionDelayReason = submissionDelayReason
        }

        await dispatch(editActions.confirmTransaction(params))
        navigate('/summary', {
          state: {
            message: `ยืนยันการรับเงิน ใบรับเงินชั่วคราว<br/>
          <b>หมายเลข ${data.eTRNo}</b><br/>
          เรียบร้อยแล้ว`,
            icon: success
          }
        })
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isOpenModal) {
      getDocument()
    }

    return () => {
      setIsShowSubmissionDelayReason(false)
      setSubmissionDelayReason('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpenModal])

  return (
    <div>
      <Modal
        isOpen={isOpenModal}
        onAfterClose={() => {
          data.reasonText = ''
          setErrors([])
        }}
        className="modal-cancel"
        style={{
          overlay: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(24, 48, 40, 0.9)'
          }
        }}
        ariaHideApp={false}
      >
        <div className="modal-cancel-content">
          {isFetching && <LoadingOverlay />}
          <div className="modal-cancel-header-text">นำส่งรายการ {data.eTRNo} เข้าบริษัท</div>
          <div className="modal-content">
            <SubmitDocumentList documentSubmit={documentSubmit} />
            <ErrorComponent errorAmount={errorAmount} errors={errors} />
          </div>
          {isShowSubmissionDelayReason && (
            <div className={Classes.FormGroup}>
              <label htmlFor="submissionDelayReason">
                <span className="require">*</span> เหตุผลที่ส่งงานล่าช้า:
              </label>
              <textarea
                className="field-input"
                id="submissionDelayReason"
                name="submissionDelayReason"
                placeholder="ระบุเหตุผล..."
                onChange={handleSubmissionDelayReasonChnage}
                value={submissionDelayReason}
              />
            </div>
          )}

          <div className="modal-cancel-group-button">
            <button className="cancel-transaction-button" onClick={() => handleCancelbutton()}>
              ยกเลิก
            </button>
            <button
              className="confirm-transaction-button"
              onClick={handleConfirmOk}
              disabled={disableSubmitBtn}
            >
              ยืนยัน
            </button>
          </div>
        </div>
      </Modal>
    </div>
  )
}
